// src/lib/sync/syncManager.js
// PATCHES IN THIS VERSION:
//
//   PHASE 5 CONSTANTS (this version):
//     QUOTA_BACKOFF_MS, PERIODIC_FLUSH_INTERVAL_MS, SCHEDULED_FLUSH_DELAY_MS
//     moved from inline literals to imports from @/config/app.constants.
//     Values are unchanged — single-source-of-truth cleanup only.
//
//   PHASE 6 IMPORT ALIASES (this version):
//     Replaced remaining relative imports with @/ aliases:
//       '../db/indexedDB'  → '@/lib/db/indexedDB'
//       '../db/firestore'  → '@/lib/db/firestore'
//       './onlineStatus'   → '@/lib/sync/onlineStatus'
//
// ── PREVIOUS PATCHES (retained) ──────────────────────────────────────────────
//
//   FIX #23: Shared subscription cache in initSync().
//
//     WHY: Previously each useSyncStore(key) call created its own independent
//     firestoreSubscribe() → onSnapshot() listener. Three components subscribing
//     to 'calendarEvents' meant 3 simultaneous Firestore listeners for the same
//     document — each firing a read on subscription AND on every change. With
//     24 store keys and multi-component pages this burned 100–200+ Firestore reads
//     per user session against the 50k/day Spark quota.
//
//     Fix: A module-level Map (_subscriptionCache) stores one active Firestore
//     subscription per key. initSync() checks the cache first — if a subscription
//     already exists, the new callback is added to its Set and a lightweight
//     unsubscribe (callback-removal only) is returned. The Firestore listener is
//     only torn down when the LAST subscriber unsubscribes. clearSyncQueueOnSignOut()
//     tears down and clears all cached subscriptions so they cannot restart for the
//     next user via the onAuthStateChanged listener inside firestoreSubscribe.
//
//     The initSync return type is unchanged (synchronous unsubscribe function).
//     No callers are affected.
//
//   FIX #24: uid stored in every pendingQueue record.
//
//     WHY: Queue records previously stored only { key, value, timestamp }. If
//     clearSyncQueueOnSignOut() failed silently and User B signed in and triggered
//     a flush, User A's queued records would be written to Firestore under User B's
//     auth token — into User B's Firestore documents.
//
//     Fix: getCurrentUid() is called in syncSet() and stored in every
//     enqueuePending() call as { key, value, uid, timestamp }. flushPendingQueue()
//     reads the current uid at flush time and only processes records whose uid
//     matches the current user. Records with a different uid are left in the queue
//     untouched (they will be cleared by their own user's next sign-out). Legacy
//     records with uid === null are processed once and will not recur.
//
//   FIX SM1+SM2: _flushRunning mutex with top-level finally{} so every exit
//     path resets the lock (0 items, quota backoff, error, normal completion).
//
//   FIX SM W1: writeBatch() replaces N individual firestoreSet() calls on flush.
//
//   FIX #21: scheduledFlush() called after failed online writes and deletes.
//   FIX #22: habitFlags consolidated into IDB_ONLY_KEYS Set.
//   FIX #13: trustFirstNull passed as getter function to firestoreSubscribe.
//   FIX #8:  Quota-aware backoff — resource-exhausted suspends flush for 1 hour.
//   FIX #9:  Parallel removePending inside flush loop via Promise.all.
//   FIX #10: Removed dead code in initSync (redundant subscriptionStarted guard).
//   FIX #6:  startPeriodicFlush / stopPeriodicFlush / startOnlineListener /
//            stopOnlineListener — flush machinery stops on sign-out.
//   FIX #7:  firestoreSubscribe trustFirstNull for cross-device tombstones.
//   FIX A2/SM6: Offline-auth uid='offline' blocked from Firestore writes.
//   FIX C2:  FIRESTORE_USERS_COLLECTION / FIRESTORE_DATA_COLLECTION imported
//            from firestore.js — single source of truth for document paths.
//   FIX #15: registerBackgroundSync() — SW flushes on reconnect while backgrounded.
//   FIX #12: STORE_BIBLE_FONT_SIZE / STORE_BIBLE_RED_LETTER added to IDB_ONLY_KEYS.
//   FIX #20: Removed auto-start of flush machinery at module-import time.
//
// ORIGINAL FIXES (retained):
//   • syncSet(key, null) is a delete — removes from IDB and writes tombstone.
//   • initSync callback passes null to onRemoteUpdate on tombstone arrival.
//   • readingPlan_* syncs to Firestore (removed from IDB-only list).

import {
  idbSet,
  idbGet,
  idbDelete,
  enqueuePending,
  getLatestPendingByKey,
  removePending,
  getPendingQueue,
  clearPendingQueue,
} from '@/lib/db/storageAdapter';
import {
  firestoreSet,
  firestoreSubscribe,
  isQuotaError,
  FIRESTORE_USERS_COLLECTION,
  FIRESTORE_DATA_COLLECTION,
} from '@/lib/db/firestore';
import { isOnline, onOnlineChange } from '@/lib/sync/onlineStatus';
import { auth, db }                 from '@/lib/firebase';
import { writeBatch, doc, serverTimestamp } from 'firebase/firestore';
import {
  STORE_SAVED_PDFS,
  STORE_HABIT_FLAGS,
  // FIX #12: Bible UI preferences are per-device (font size, red-letter toggle).
  // They carry no user data and change too frequently to justify Firestore quota cost.
  // Adding them here routes their writes through IDB-only pub/sub, not Firestore.
  STORE_BIBLE_FONT_SIZE,
  STORE_BIBLE_RED_LETTER,
  STORE_BIBLE_TRANSLATION,
  STORE_MEMORY_VERSE_SETTINGS,
} from '@/lib/storeKeys';
import {
  QUOTA_BACKOFF_MS,
  PERIODIC_FLUSH_INTERVAL_MS,
  SCHEDULED_FLUSH_DELAY_MS,
} from '@/config/app.constants';

// ── Auth helper ───────────────────────────────────────────────────────────────
// FIX #24: Centralised uid accessor so syncSet() and flushPendingQueue() both
// read the same source of truth. Returns null when no user is signed in.
function getCurrentUid() {
  return auth?.currentUser?.uid ?? null;
}

// FIX SM1+SM2: Renamed from _flushScheduled to _flushRunning to reflect the
// actual semantics (mutex, not a pending-work flag). Previously named
// "_flushScheduled" but used as a "currently executing" guard — the name
// implied the flush was merely scheduled, not that it was actively running.
//
// The critical SM1 bug: the flag was set to true at the top of
// flushPendingQueue(), but the only reset (= false) was inside a try/catch
// that was nested inside the batch-commit block. Any early return before that
// block (0 items, quota backoff, not online) left the flag permanently true,
// meaning no further flush could ever run until the page was reloaded.
//
// Fix: the flag is now reset in a top-level finally{} that wraps the entire
// function body, so every exit path — early return, thrown error, or normal
// completion — resets the lock.
let _flushRunning = false;
let _flushTimer   = null;

// ── Sync error bus ────────────────────────────────────────────────────────────
// Provides a lightweight pub/sub for sync failures so UI layers can surface
// errors to users without this module depending on React.
//
// Usage (consumer):
//   import { onSyncError } from '@/lib/sync/syncManager';
//   useEffect(() => {
//     return onSyncError(({ context, error }) => {
//       toast.error(`Sync failed: ${context}`);
//     });
//   }, []);
//
// Only ACTIONABLE errors are emitted (flush failures, sign-out queue failures).
// Informational events (quota backoff, batch fallback) stay as console.warn.
const _syncErrorHandlers = new Set();

/** Register a sync error listener. Returns an unsubscribe function. */
export function onSyncError(fn) {
  _syncErrorHandlers.add(fn);
  return () => _syncErrorHandlers.delete(fn);
}

/** Internal: emit a sync error to all registered handlers. */
function _emitSyncError(context, error) {
  console.error(`[SyncManager] ${context}:`, error);
  _syncErrorHandlers.forEach(fn => {
    try { fn({ context, error, timestamp: Date.now() }); } catch { /* handler must not throw */ }
  });
}

// ── Quota event bus (Flag 16/25) ─────────────────────────────────────────────
// Lightweight pub/sub so useFirestoreQuota can track LOCAL read/write activity
// without syncManager.js taking a React dependency. Mirrors the onSyncError
// pattern above.
//
// Usage (consumer):
//   import { onQuotaEvent } from '@/lib/sync/syncManager';
//   useEffect(() => onQuotaEvent(({ type, count }) => {
//     if (type === 'write') trackWrite(count); else trackRead(count);
//   }), []);
const _quotaEventHandlers = new Set();

/** Register a quota event listener. Returns an unsubscribe function. */
export function onQuotaEvent(fn) {
  _quotaEventHandlers.add(fn);
  return () => _quotaEventHandlers.delete(fn);
}

/** Internal: emit a quota event ('write' | 'read') to all registered handlers. */
function _emitQuotaEvent(type, count = 1) {
  _quotaEventHandlers.forEach(fn => {
    try { fn({ type, count, timestamp: Date.now() }); } catch { /* handler must not throw */ }
  });
}

// FIX #6: Track the periodic flush interval ID so we can clear it on sign-out.
let _periodicFlushInterval = null;

// FIX #8: Quota backoff — after a resource-exhausted error, pause flushing
// for QUOTA_BACKOFF_MS so we don't hammer the daily limit further.
// Value: QUOTA_BACKOFF_MS from app.constants (1 hour).
let _quotaBackoffUntil = 0; // epoch ms; 0 = no active backoff

// ── Flush pending queue ───────────────────────────────────────────────────────
export async function flushPendingQueue() {
  if (!isOnline())   return;
  if (_flushRunning) return; // FIX SM1+SM2: mutex — one flush at a time

  // FIX #8: Honour quota backoff — do not retry until the window expires.
  if (Date.now() < _quotaBackoffUntil) {
    const remainingMin = Math.ceil((_quotaBackoffUntil - Date.now()) / 60_000);
    console.warn(`[SyncManager] Quota backoff active — skipping flush for ~${remainingMin} min.`);
    return;
  }

  // FIX SM1+SM2: Set the running lock HERE, then wrap everything in try/finally
  // so the lock is guaranteed to reset on every exit path:
  //   • 0 items found (early return)          ← was broken before this fix
  //   • quota backoff hit mid-flush            ← was broken before this fix
  //   • batch.commit() throws                  ← was partially working
  //   • normal completion                      ← was working
  _flushRunning = true;

  try {
    // FIX #24: Only flush records that belong to the currently signed-in user.
    const currentUid = getCurrentUid();

    const latestByKey = await getLatestPendingByKey(currentUid);
    const keys        = Object.keys(latestByKey);
    if (keys.length === 0) return;

    console.info(`[SyncManager] Flushing ${keys.length} key(s) for uid=${currentUid ?? 'legacy'}…`);

    const allPending = await getPendingQueue();

    // FIX SM W1: Use writeBatch() instead of N individual firestoreSet() calls.
    // Firestore's writeBatch handles up to 500 ops atomically — on a reconnect
    // after an offline gap with 10 queued keys this cuts write-quota usage by ~10x.
    // Quota errors and network errors are still caught and handled per the
    // existing backoff / retry strategy.
    //
    // NOTE: writeBatch cannot mix deletes and sets in the same call when some
    // values are null (tombstones). We split into two passes:
    //   1. Collect all ops into the batch (set for values, delete for nulls).
    //   2. Commit once. On success, remove all matching pending records.
    //   3. On quota error, abort immediately. On other errors, fall through to
    //      individual retry so one bad key doesn't block the rest.

    // FIX C2: Use imported constants from firestore.js — single source of truth
    // for the Firestore document path. Previously hardcoded 'users'/'data' here
    // and in firestore.js independently; a rename in one file would silently
    // route batch writes to different documents than onSnapshot reads.
    const COLLECTION = FIRESTORE_USERS_COLLECTION;
    const DATA_COLL  = FIRESTORE_DATA_COLLECTION;

    const batch = writeBatch(db);
    const batchedKeys = [];

    for (const key of keys) {
      const latest = latestByKey[key];
      const ref = doc(db, COLLECTION, currentUid, DATA_COLL, key);

      if (latest.value === null) {
        // Tombstone — tell other devices this key was deleted
        batch.set(ref, { value: null, updatedAt: serverTimestamp() }, { merge: true });
      } else {
        batch.set(ref, { value: latest.value, updatedAt: serverTimestamp() }, { merge: true });
      }
      batchedKeys.push(key);
    }

    try {
      await batch.commit();
      _emitQuotaEvent('write', batchedKeys.length);

      // Batch succeeded — remove all pending records for these keys
      const toRemove = allPending.filter(
        (item) =>
          batchedKeys.includes(item.key) &&
          (item.uid === currentUid || item.uid == null)
      );
      await Promise.all(toRemove.map((item) => removePending(item.id)));

      console.info(`[SyncManager] Batch flush committed ${batchedKeys.length} key(s).`);

    } catch (err) {
      if (isQuotaError(err)) {
        _quotaBackoffUntil = Date.now() + QUOTA_BACKOFF_MS;
        console.warn(
          `[SyncManager] Firestore quota exhausted — pausing sync for 1 hour.`,
          err
        );
        return;
      }

      // Batch failed (network error etc.) — fall back to individual writes
      // so one bad key doesn't block the rest from flushing.
      console.warn(`[SyncManager] Batch commit failed, retrying individually:`, err);

      for (const key of batchedKeys) {
        const latest = latestByKey[key];
        try {
          await firestoreSet(key, latest.value);
          _emitQuotaEvent('write', 1);

          const toRemove = allPending.filter(
            (item) =>
              item.key === key &&
              (item.uid === currentUid || item.uid == null)
          );
          await Promise.all(toRemove.map((item) => removePending(item.id)));

        } catch (keyErr) {
          if (isQuotaError(keyErr)) {
            _quotaBackoffUntil = Date.now() + QUOTA_BACKOFF_MS;
            console.warn(`[SyncManager] Quota exhausted mid-retry — pausing 1 hour.`, keyErr);
            return;
          }
          console.error(`[SyncManager] Failed to flush "${key}":`, keyErr);
          _emitSyncError(`Failed to flush "${key}"`, keyErr);
          // Leave in queue — retry on next flush
        }
      }
    }

  } finally {
    // FIX SM1+SM2: Top-level finally — always resets the running lock.
    // This finally wraps the ENTIRE function body (including the early returns
    // for 0 items and quota backoff) so _flushRunning is reset on every exit.
    _flushRunning = false;
  }
}

// ── Periodic retry setup / teardown ───────────────────────────────────────────
// FIX #6: startPeriodicFlush / stopPeriodicFlush are now called explicitly
// so that the interval doesn't keep running after sign-out.
// Interval value: PERIODIC_FLUSH_INTERVAL_MS from app.constants (5 minutes).
function startPeriodicFlush() {
  if (_periodicFlushInterval) return; // already running
  _periodicFlushInterval = setInterval(() => {
    if (isOnline()) flushPendingQueue();
  }, PERIODIC_FLUSH_INTERVAL_MS);
}

function stopPeriodicFlush() {
  if (_periodicFlushInterval) {
    clearInterval(_periodicFlushInterval);
    _periodicFlushInterval = null;
  }
}

// FIX #6: onOnlineChange unsubscribe handle so we can remove the listener
// on sign-out and re-add it for the next session.
let _unsubscribeOnline = null;

function startOnlineListener() {
  if (_unsubscribeOnline) return; // already listening
  _unsubscribeOnline = onOnlineChange((online) => {
    if (online) flushPendingQueue();
  });
}

function stopOnlineListener() {
  if (_unsubscribeOnline) {
    _unsubscribeOnline();
    _unsubscribeOnline = null;
  }
}

// FIX #20: Do NOT auto-start here.
// These lines previously started the flush machinery at module-import time,
// which happens before any user has authenticated. The periodic flush and
// online listener would then call firestoreSet() with no auth context,
// causing silent permission errors.
//
// syncSet() already calls startOnlineListener() + startPeriodicFlush() at
// the top of every write — the first authenticated write re-arms both.
// For a user who signs in and immediately reads without writing, initSync()
// calls firestoreSubscribe() which handles its own connection lifecycle.
//
// startOnlineListener();  ← removed
// startPeriodicFlush();   ← removed

// ── BackgroundSync (FIX #15) ──────────────────────────────────────────────────
// Registers a one-shot sync tag with the SW so the browser will flush the
// pending queue when it regains connectivity even while the app is backgrounded.
//
// The SW cannot call Firestore directly (different execution context). Instead
// it posts a 'BG_SYNC_FLUSH' message to all open window clients, and the
// listener below forwards that to flushPendingQueue().
//
// Fallback: if BackgroundSync is unsupported (iOS Safari < 16.4, Firefox),
// the periodic 5-minute flush and the online-state listener already cover
// the reconnect scenario whenever the tab is foregrounded. No data is lost.
async function registerBackgroundSync() {
  try {
    if (!('serviceWorker' in navigator) || !('SyncManager' in window)) return;
    const reg = await navigator.serviceWorker.ready;
    await reg.sync.register('pending-flush');
  } catch {
    // BackgroundSync not supported or SW not registered — silent no-op.
    // The existing periodic-flush and online-listener are the fallback.
  }
}

// Module-level SW message listener — runs once when syncManager is first imported.
// Receives 'BG_SYNC_FLUSH' from the SW's sync event handler and triggers a flush.
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'BG_SYNC_FLUSH') {
      console.info('[SYNC] BackgroundSync flush triggered by service worker.');
      flushPendingQueue();
    }
  });
}

// ── App-resume flush (Capacitor native only) ───────────────────────────────
// PERIODIC_FLUSH_INTERVAL_MS (5 min) does not fire while iOS fully suspends
// the app in the background, so a device that was offline for hours would
// otherwise wait up to 5 minutes after reconnecting before the queue starts
// flushing. On native builds, listen for the app returning to the foreground
// and flush immediately. This is a no-op on web — Capacitor.isNativePlatform()
// is false in a browser/PWA context, so this adds no behaviour change there.
if (typeof window !== 'undefined') {
  import('@capacitor/core').then(({ Capacitor }) => {
    if (!Capacitor.isNativePlatform()) return;
    import('@capacitor/app').then(({ App: CapacitorApp }) => {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          console.info('[SyncManager] App resumed from background — flushing queue.');
          flushPendingQueue();
        }
      });
    }).catch((err) => {
      console.warn('[SyncManager] @capacitor/app not available — resume-flush disabled.', err);
    });
  });
}

/**
 * Call this during sign-out BEFORE Firebase signOut() to prevent queued
 * writes from user A being flushed under user B's auth context on the next
 * session.
 *
 * FIX #6: Now also stops the periodic flush interval and the online listener
 * so neither fires after sign-out with no authenticated user.
 *
 * FIX #23: Now tears down all cached Firestore subscriptions so that they
 * cannot restart for the next user when onAuthStateChanged fires. Without
 * this, old subscription closures (created for user A) would reconnect
 * when user B signs in and deliver data to potentially stale callbacks.
 *
 * Full sign-out checklist (enforced in AuthContext / sign-out handler):
 *   1. await clearSyncQueueOnSignOut()   ← this (stops listeners + clears cache)
 *   2. await clearAppData()              ← from indexedDB.js
 *   3. await clearPendingQueue()         ← from indexedDB.js
 *   4. Firebase signOut()
 *   5. redirect to login
 */
export async function clearSyncQueueOnSignOut() {
  clearTimeout(_flushTimer);
  _flushTimer        = null;
  _flushRunning      = false; // FIX SM1+SM2: renamed from _flushScheduled
  _quotaBackoffUntil = 0;    // FIX #8: reset backoff on sign-out
  _idbOnlyListeners.clear();

  // FIX #6: Stop background flush machinery so it doesn't fire post sign-out.
  stopPeriodicFlush();
  stopOnlineListener();

  // FIX #23: Tear down all cached Firestore subscriptions.
  // Calling unsubFirestore() sets the `cancelled` flag inside each
  // firestoreSubscribe closure, preventing them from restarting when
  // onAuthStateChanged fires for the next user.
  for (const { unsubFirestore } of _subscriptionCache.values()) {
    try { unsubFirestore(); } catch { /* ignore — already unsubscribed */ }
  }
  _subscriptionCache.clear();

  try {
    await clearPendingQueue();
    console.info('[SyncManager] Pending queue cleared on sign-out.');
  } catch (err) {
    console.error('[SyncManager] Failed to clear pending queue on sign-out:', err);
    _emitSyncError('Failed to clear pending queue on sign-out', err);
  }
}

// ── Debounced flush trigger ───────────────────────────────────────────────────
// Delay value: SCHEDULED_FLUSH_DELAY_MS from app.constants (2 seconds).
function scheduledFlush() {
  clearTimeout(_flushTimer);
  _flushTimer = setTimeout(flushPendingQueue, SCHEDULED_FLUSH_DELAY_MS);
}

// ── In-memory pub/sub for IDB-only keys ──────────────────────────────────────
// IDB-only keys (e.g. habitFlags) are never written to Firestore, so
// firestoreSubscribe never fires for them. Without this, two useSyncStore
// instances subscribing to the same IDB-only key in the same session (e.g.
// Bible writing habitFlags, Dashboard reading it) would never see each other's
// writes — Dashboard would only pick up the new value on its next full mount.
const _idbOnlyListeners = new Map(); // key → Set<callback>

function notifyIdbOnly(key, value) {
  const listeners = _idbOnlyListeners.get(key);
  if (!listeners) return;
  listeners.forEach((cb) => cb(value));
}

function subscribeIdbOnly(key, cb) {
  if (!_idbOnlyListeners.has(key)) _idbOnlyListeners.set(key, new Set());
  _idbOnlyListeners.get(key).add(cb);
  return () => _idbOnlyListeners.get(key)?.delete(cb);
}

// ── IDB-only key list ─────────────────────────────────────────────────────────
// FIX: Replaced bare string literals with STORE_* constants from storeKeys.js.
// A typo here would silently stop Firestore sync for that key with no error.
const IDB_ONLY_KEYS = new Set([
  STORE_SAVED_PDFS,       // large base64 binary — would exceed Firestore's 1 MB document limit
  STORE_HABIT_FLAGS,      // high-frequency daily-habit writes — IDB-only to avoid quota burn
  // FIX #12: UI preferences that are per-device and change too frequently to
  // sync to Firestore. Quota saved: ~2 writes per font-size tap, ~1 per toggle.
  STORE_BIBLE_FONT_SIZE,  // 'sm'|'md'|'lg'|'xl' — per-device reading preference
  STORE_BIBLE_RED_LETTER, // boolean — per-device red-letter display preference
  STORE_BIBLE_TRANSLATION, // 'kjv'|'bsb' — per-device translation preference
  STORE_MEMORY_VERSE_SETTINGS, // reminder time / popup state — per-device
]);

function isIdbOnly(key) {
  return IDB_ONLY_KEYS.has(key);
}

// ── Shared subscription cache (FIX #23) ──────────────────────────────────────
// One live Firestore listener per key, shared across all useSyncStore instances.
//
// Structure:
//   Map<key, {
//     unsubFirestore : () => void        — tears down the firestoreSubscribe closure
//     callbacks      : Set<Function>     — all active onRemoteUpdate handlers
//   }>
//
// Lifecycle:
//   • First initSync(key) call  → creates Firestore subscription, stores in cache
//   • Subsequent calls          → add callback to existing Set, return lightweight unsub
//   • Last subscriber unsubs    → tears down Firestore subscription, removes from cache
//   • clearSyncQueueOnSignOut() → tears down ALL subscriptions, clears cache entirely
const _subscriptionCache = new Map();

// ── Write ─────────────────────────────────────────────────────────────────────
/**
 * Persist a value through all three layers.
 *
 * Passing null is a DELETE — the key is removed from IDB and a null tombstone
 * is written to Firestore so other devices know to clear it on next sync.
 *
 * @param {string} key   — any app data key
 * @param {any}    value — JSON-serialisable, or null to delete
 */
export async function syncSet(key, value, { skipIdbWrite = false } = {}) {
  // FIX #6: Restart the flush machinery if it was stopped by sign-out and a
  // new user is now writing (i.e. they've signed in again in the same tab).
  startOnlineListener();
  startPeriodicFlush();

  // FIX #24: Capture uid at write time and store it with the queue record so
  // flushPendingQueue() can verify ownership before writing to Firestore.
  const uid = getCurrentUid();

  // FIX A2/SM6: Block Firestore writes when running in offline-auth mode.
  //
  // AuthContext sets user = { uid: 'offline', ... } when Firebase Auth is
  // unavailable (missing/invalid API key or auth init failure). If the device
  // has network connectivity, isOnline() returns true and syncSet() would call
  // firestoreSet() with uid='offline', silently creating a
  // `users/offline/data/<key>` document — a cross-user data leak that also
  // burns Firestore quota.
  //
  // When uid is 'offline': IDB write proceeds normally (app works as expected),
  // but every Firestore path and the pending queue are skipped entirely.
  // IDB-only pub/sub (notifyIdbOnly) is unaffected.
  const isOfflineUser = (uid === 'offline');

  if (value === null) {
    // ── DELETE path ──────────────────────────────────────────────────────────
    await idbDelete(key);

    if (isIdbOnly(key)) {
      notifyIdbOnly(key, null);
      return;
    }

    // FIX A2/SM6: Skip Firestore entirely for offline-auth users.
    if (isOfflineUser) return;

    if (isOnline()) {
      try {
        await firestoreSet(key, null);
        _emitQuotaEvent('write', 1);
      } catch (err) {
        console.warn(`[SyncManager] Firestore delete failed for "${key}", queuing`, err);
        await enqueuePending(key, null, uid); // FIX #24: include uid
        scheduledFlush(); // retry after SCHEDULED_FLUSH_DELAY_MS
      }
    } else {
      await enqueuePending(key, null, uid); // FIX #24: include uid
      scheduledFlush();
      registerBackgroundSync(); // FIX #15: let SW flush when browser reconnects
    }
    return;
  }

  // ── WRITE path ───────────────────────────────────────────────────────────
  // FIX (Flag 32): skipIdbWrite lets a caller that already wrote this value to
  // IDB itself (useSyncStore's debounced runPersist) opt out of a second,
  // redundant idbSet() here. Defaults to false so no other caller is affected.
  if (!skipIdbWrite) await idbSet(key, value);

  if (isIdbOnly(key)) {
    notifyIdbOnly(key, value);
    return;
  }

  // FIX A2/SM6: Skip Firestore entirely for offline-auth users.
  if (isOfflineUser) return;

  if (isOnline()) {
    try {
      await firestoreSet(key, value);
      _emitQuotaEvent('write', 1);
    } catch (err) {
      console.warn(`[SyncManager] Firestore write failed for "${key}", queuing`, err);
      await enqueuePending(key, value, uid); // FIX #24: include uid
      scheduledFlush(); // retry after SCHEDULED_FLUSH_DELAY_MS
    }
  } else {
    await enqueuePending(key, value, uid); // FIX #24: include uid
    scheduledFlush();
    registerBackgroundSync(); // FIX #15: let SW flush when browser reconnects
  }
}

// ── Real-time inbound sync ────────────────────────────────────────────────────
/**
 * Subscribe to changes for a key. Returns a synchronous unsubscribe function.
 *
 * FIX #23: Uses a shared subscription cache. All useSyncStore(key) instances
 * share a single Firestore onSnapshot listener per key. The listener is only
 * created on the first subscriber and only torn down when the last subscriber
 * unsubscribes. This reduces Firestore reads from O(components × keys) to
 * O(keys) per session — critical for staying within the 50k/day Spark quota.
 *
 * FIX #13: trustFirstNull is passed as a getter function () => trust.firstNull
 * so firestoreSubscribe reads it at snapshot-event time, not subscription time.
 * The async IDB check updates trust.firstNull after resolving — by which point
 * the first snapshot event has not yet fired in practice. This makes cross-device
 * tombstone delivery (null from Firestore when IDB has data) work correctly.
 *
 * @param {string}   key
 * @param {Function} onRemoteUpdate  receives the remote value, or null on delete
 * @returns {Function} unsubscribe
 */
export function initSync(key, onRemoteUpdate) {
  // IDB-only keys never reach Firestore — use the in-process pub/sub instead.
  if (isIdbOnly(key)) return subscribeIdbOnly(key, onRemoteUpdate);

  // FIX #23: Return existing subscription if one is already active for this key.
  if (_subscriptionCache.has(key)) {
    const entry = _subscriptionCache.get(key);
    entry.callbacks.add(onRemoteUpdate);

    return () => {
      entry.callbacks.delete(onRemoteUpdate);
      // Only tear down the Firestore listener when the last subscriber leaves.
      if (entry.callbacks.size === 0) {
        try { entry.unsubFirestore(); } catch { /* ignore */ }
        _subscriptionCache.delete(key);
      }
    };
  }

  // No existing subscription — create one and register it in the cache.
  const callbacks = new Set([onRemoteUpdate]);

  // FIX #13: Mutable trust container — updated by the async IDB check below.
  // A getter closure is passed to firestoreSubscribe so the value is read at
  // snapshot-event time, not at subscription-creation time (always false).
  const trust = { firstNull: false };

  idbGet(key)
    .then((localValue) => { trust.firstNull = (localValue !== null); })
    .catch(() => { /* leave false — default safe behaviour */ });

  const unsubFirestore = firestoreSubscribe(key, async (remoteValue) => {
    _emitQuotaEvent('read', 1);
    if (remoteValue === null) {
      // firestoreSubscribe already checked resolveTrust() before calling here.
      await idbDelete(key);
      callbacks.forEach((cb) => cb(null));
      return;
    }
    await idbSet(key, remoteValue);
    callbacks.forEach((cb) => cb(remoteValue));
  }, { trustFirstNull: () => trust.firstNull });

  _subscriptionCache.set(key, { unsubFirestore, callbacks });

  return () => {
    callbacks.delete(onRemoteUpdate);
    // Tear down the Firestore listener when the last subscriber leaves.
    if (callbacks.size === 0) {
      try { unsubFirestore(); } catch { /* ignore */ }
      _subscriptionCache.delete(key);
    }
  };
}